import { useState, useEffect, useRef, useCallback } from 'react';
import './FloatingModal.scss';
import { CloseOutlined, ExpandOutlined, LineOutlined } from '@ant-design/icons';
import { t } from '../../i18n/i18n';
import usePressEsc from '../../hooks/usePressEsc';
import useClickOutside from '../../hooks/useClickOutside';

const FloatingModal = ({ show, onClose, children, title = 'Modal', className = '' }) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const modalRef = useRef(null);

  const handleMinimize = useCallback(() => {
    setIsMinimized((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setIsMinimized(false);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!show) {
      setIsClosing(true);
      const timer = setTimeout(() => {
        setIsVisible(false);
        setIsClosing(false);
      }, 300);
      return () => clearTimeout(timer);
    }

    setIsVisible(true);
    setIsClosing(false);
  }, [show]);

  usePressEsc(handleClose);

  useClickOutside(modalRef, handleClose);

  if (!isVisible) return null;

  return (
    <div
      className={`floating-modal ${isMinimized ? 'minimized' : ''} ${
        isClosing ? 'closing' : 'opening'
      } ${className}`}
    >
      <div className="floating-modal-header">
        <h4 className="floating-modal-title">{title}</h4>
        <div className="floating-modal-controls">
          <button
            type="button"
            className="floating-modal-minimize"
            onClick={handleMinimize}
            title={isMinimized ? t('EXPAND') : t('MINIMIZE')}
          >
            {isMinimized ? <ExpandOutlined /> : <LineOutlined />}
          </button>
          <button
            type="button"
            className="floating-modal-close"
            onClick={handleClose}
            title={t('CLOSE')}
          >
            <CloseOutlined />
          </button>
        </div>
      </div>

      {!isMinimized && <div className="floating-modal-content">{children}</div>}
    </div>
  );
};

export default FloatingModal;
